import { EmptySport, Loader, useScommesseFallback } from 'components/scommessaFallback';
import { Icon, IconsEnum } from 'components/Icons';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ScommesseFilterTertiary, toRegex } from 'components/scommesseFilterTertiary';
import { SportsCacheTemplateAvvenimentoDto, SportsTipoScommessa, SportsToolTipInfoScommessa } from 'types/swagger';
import { breakpoint, isHappybetSite } from 'utility/constant';
import { decodePathname, getScommesse, selectAvvenimentoSlug } from 'features/sport';
import { isNotEmpty, isTruthy } from 'utility/functions';
import { resetFilterAvvenimento, setIsInfoOpen } from 'features/sport/sportSlice';
import { selectIsVisualizzazioneEstesa } from 'features/settings/selectors';
import { useAppDispatch, useTypedSelector } from 'lib/centralStore';
import { useGetLabelsByKeys, useTranslation } from 'hooks/useLingUI';
import { ApiStatus } from 'features/api/thunkUtils';
import { AvvenimentoMarcatoriTable } from './AvvenimentoMarcatoriTable';
import { BannerHTML5, enBannerHTML5 } from 'components/bannerHTML5';
import { BannerHeroRenderer } from 'components/banner/bannerHero';
import { Button } from 'components/button';
import { Category } from 'types/categoryEnum';
import { HeaderTitleWithSearch } from 'components/headerTitleWithSearch';
import { ItemType } from 'components/scommesseFilterTertiary/ScommesseFilterTertiary';
import { Modal } from 'components/modals/components/modal';
import { ScommesseAccordionFilterPrimary } from 'components/scommesseAccordionFilterPrimary/ScommesseAccordionFilterPrimary';
import { ScommesseConditionalAccordion } from 'components/scommesseConditionalAccordion';
import { ScommesseFiltersList } from 'components/scommesseFiltersList';
import { ScommesseFiltersListScrollable } from 'components/scommesseFiltersListScrollable/ScommesseFiltersListScrollable';
import { ScommesseFiltersListSecondary } from 'components/scommesseFiltersListSecondary';
import { ScommesseLayout } from 'features/scommesseLayout';
import { ScommesseTitle } from 'components/scommesseTitle';
import { SportAvvenimentoHeader } from 'features/sport/components/sportAvvenimentoHeader';
import { SportAvvenimentoPageQuery } from 'lib/datoCms/queries/getSportAvvenimentoPage';
import { SportComposer } from 'features/sport/components/sportComposer';
import { SportInternalLayout } from 'features/sport/components/sportInternalLayout';
import { SportTopNavigation } from 'features/sport/components/sportTopNavigation';
import { TabsWithAccordion } from 'components/tabsWithAccordion';
import { TabsWithAccordionType } from 'lib/datoCms/queries/getSeoData';
import { Taxes } from 'features/sport/components/sportTicket/sportTicketSlice';
import { Wrapper } from 'components/wrapper';
import { errorLabelsUpdated } from 'features/carrello/carrelloSlice';
import { selectIsSportSearchEnabled } from 'features/configuration/selectors';
import { setIsVisualizzazioneEstesa } from 'features/settings/settingsSlice';
import { shallowEqual } from 'fast-equals';
import styles from './PageContent.module.scss';
import { useFilterSelection } from 'features/sport/hooks';
import { useRouter } from 'next/router';
import useScommessaListScrollable from 'hooks/useScommessaListScrollable';
import useWindowSize, { enComparer } from 'hooks/useWindowSize';
import { SportNewsDto } from 'lib/ssr/sport/news/types';
import { SportNews } from 'components/sportNews';

export type Props = SportAvvenimentoPageQuery & {
  news?: SportNewsDto;
  taxes?: Taxes;
  seoData?: TabsWithAccordionType;
};

export interface FilterItemType {
  label: string;
  hasNew: boolean;
  labelTrKey: string;
  scommesseDescription?: Array<SportsToolTipInfoScommessa>;
}

export type FilterType = Array<FilterItemType> | null;

const PageContent = ({ news, taxes, seoData, sportNav, allCarrelloErrors, sportAvvenimentoPage }: Props) => {
  const { t } = useTranslation();
  const { asPath, isReady } = useRouter();
  const dispatch = useAppDispatch();

  const [scommessaGroupLabel, extendedVisualizationLabel, scommessaFilterBy, makeYourBetLabel, estesaSwitchLabel] =
    useGetLabelsByKeys([
      'scommessa-group',
      'extended-visualization',
      'scommessa-filter-by',
      'make-your-bet',
      'estesa-switch',
    ]);

  const refContainer = useRef<HTMLDivElement>(null);
  const { showEmpty, showLoader } = useScommesseFallback({ container: refContainer.current, isReady });

  const { slugFromPath, disciplina } = useMemo(() => {
    const slugFromPath = decodePathname(asPath);
    const [_, disciplina] = slugFromPath.split('/');
    return { slugFromPath, disciplina };
  }, [asPath]);

  const { doCheck } = useWindowSize();
  const [isMobile, isDesktopXl] = doCheck([
    { comparer: enComparer.less, size: breakpoint.md },
    { comparer: enComparer.greater, size: breakpoint.xl },
  ]);

  const { id, heroBanner, visualComposer, sportNewsBlock } = sportAvvenimentoPage ?? {};

  // state
  const [valueSecondaryFilter, _setValueSecondaryFilter] = useState<string>('');
  const [selectedPrimaryFilter, setSelectedPrimaryFilter] = useState<number>(0);
  // stato filtri
  const [filterValue, setFilterValue] = useState<string | undefined>();
  const [checkedItems, setCheckedItems] = useState<undefined | { [name: string]: boolean }>({});

  const isOpen = useTypedSelector(({ carrello }) => carrello.isOpen);
  const isSportSearchEnabled = useTypedSelector(selectIsSportSearchEnabled);
  const isVisualizzazioneEstesa = useTypedSelector(selectIsVisualizzazioneEstesa);
  const { template, avvenimentoKey, avvenimentoFilter, infoTipoScommessaMap, isTemplateLoading, isScommessaLoading } =
    useTypedSelector(({ sport }) => {
      const {
        scommessa,
        scommessaStatus,
        avvenimentoFilter,
        templateAvvenimento: template,
        templateAvvenimentoStatus,
      } = sport ?? {};
      const { avvenimentoList, infoTipoScommessaMap } = scommessa ?? {};
      const [avvScommessa] = avvenimentoList ?? [];

      return {
        template: template as SportsCacheTemplateAvvenimentoDto,
        avvScommessa,
        avvenimentoKey: avvScommessa?.key,
        isTemplateLoading: templateAvvenimentoStatus === ApiStatus.loading,
        isScommessaLoading: scommessaStatus === ApiStatus.loading,
        avvenimentoFilter,
        infoTipoScommessaMap,
      };
      // TODO : memoize
    }, shallowEqual);

  const slugDefault = useTypedSelector(selectAvvenimentoSlug);
  const selectedGruppo = isTemplateLoading ? undefined : Reflect.get(template?.gruppoList ?? {}, selectedPrimaryFilter);

  const isMarcatore = useMemo(() => isTruthy(selectedGruppo?.isMarcatori), [selectedGruppo]);
  const selectedSlug = useMemo(() => {
    let slug = slugFromPath;

    if (selectedGruppo && isNotEmpty(selectedGruppo?.slug)) {
      slug = selectedGruppo?.slug!;
    } else if (isNotEmpty(slugDefault)) {
      slug = slugDefault!;
    }
    return slug;
  }, [selectedGruppo, slugDefault, slugFromPath]);

  const refSlug = useRef('');

  useEffect(() => {
    if (refSlug.current !== selectedSlug) {
      refSlug.current = selectedSlug;
      dispatch(getScommesse({ slug: selectedSlug, isMarcatore }));
    }
  }, [isMarcatore, selectedSlug, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(setIsInfoOpen(false));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch(errorLabelsUpdated(allCarrelloErrors));
  }, [dispatch, allCarrelloErrors]);

  const tipoScommessaList = useMemo(() => {
    let raw: Array<SportsTipoScommessa> | undefined;
    if (isTruthy(avvenimentoFilter?.length)) {
      // transform options to regex string
      const regex = toRegex(avvenimentoFilter);
      // get matches
      raw = selectedGruppo?.tipoScommessaList?.filter((tipoScommessa) =>
        tipoScommessa?.infoTipoScommessaKeyStringList?.some((info) => {
          const descrizione = `${infoTipoScommessaMap?.[info]?.descrizione}`;
          return isTruthy(descrizione.match(regex)?.length);
        })
      );
    } else {
      raw = template?.gruppoList?.[selectedPrimaryFilter]?.tipoScommessaList;
    }

    return raw
      ?.map((e) => e!)
      .sort(
        (
          { priorita: prA, descrizione: desA, descrizioneTrKey: desKa },
          { priorita: prB, descrizione: desB, descrizioneTrKey: desKb }
        ) => {
          if (prA === prB) {
            return `${t(desKb, desB)}` > `${t(desKa, desA)}` ? -1 : 1;
          }
          return prA - prB;
        }
      );
  }, [
    avvenimentoFilter,
    selectedGruppo?.tipoScommessaList,
    infoTipoScommessaMap,
    template?.gruppoList,
    selectedPrimaryFilter,
    t,
  ]);

  const filteredGruppoList: FilterType = useMemo(() => {
    return (template?.gruppoList ?? []).map(
      ({ descrizione, descrizioneTrKey, isNew }) =>
        ({
          label: descrizione,
          hasNew: isNew,
          labelTrKey: descrizioneTrKey,
        } as FilterItemType)
    );
  }, [template]);

  const gruppoScommessaListScrollable = useScommessaListScrollable(filteredGruppoList);

  useEffect(() => {
    dispatch(resetFilterAvvenimento());
  }, [dispatch, slugFromPath]);

  useFilterSelection(filterValue ?? '', checkedItems ?? {});

  useEffect(() => {
    if (avvenimentoFilter.length === 0 && selectedGruppo?.textualFilters) {
      selectedGruppo?.textualFilters!.map((filter) => {
        setCheckedItems({ [filter.value!]: false });
      });
    }
  }, [avvenimentoFilter, selectedGruppo?.textualFilters]);

  useEffect(() => {
    if (valueSecondaryFilter) {
      tipoScommessaList?.filter((item) => item.descrizione === valueSecondaryFilter);
    }
  }, [valueSecondaryFilter, tipoScommessaList]);

  const onGruppoClickHanlder = (selectedFilter?: number) => {
    dispatch(resetFilterAvvenimento());
    setCheckedItems({});
    setFilterValue(undefined);
    setSelectedPrimaryFilter(selectedFilter ?? 0);
  };

  const onFilterClickHandler = (e: HTMLInputElement) => {
    setCheckedItems((prevCheckedItems) => ({
      ...prevCheckedItems,
      [e.name]: e.checked,
    }));
    setFilterValue(e.value);
  };

  const [isWindowFiltersOpen, setIsWindowFiltersOpen] = useState(false);

  const renderContent = () => {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    let objGruppi = <React.Fragment key={`Gruppo-List-${asPath}`} />;

    if (template) {
      // TODO : rifare
      // const selectedItem = checkedItems
      //   ? checkedItems[
      //       `${selectedGruppo?.textualFilters?.flatMap((filter) => filter.value)}` as keyof typeof checkedItems
      //     ]
      //   : false;

      if (isTruthy(template?.gruppoList?.length) && selectedGruppo) {
        objGruppi = (
          <React.Fragment key={`Gruppo-List-${asPath}`}>
            {isMobile ? (
              <React.Fragment key={`Mobile-List-${asPath}-Mobile`}>
                <ScommesseFiltersListScrollable
                  selectedItem={selectedPrimaryFilter}
                  color={'primary'}
                  onSelectedElement={(index) => {
                    setSelectedPrimaryFilter(index);
                  }}
                  className={styles.gruppoMobileContainer}
                  list={gruppoScommessaListScrollable}
                />
                {isTruthy(selectedGruppo?.textualFilters?.length) && isWindowFiltersOpen && (
                  <Modal
                    modalType="fullheight"
                    modalCategory="mobile-filter-modal"
                    onClose={() => setIsWindowFiltersOpen(false)}
                  >
                    <div className={styles.wrapperFilters}>
                      <ScommesseFilterTertiary
                        title={scommessaFilterBy}
                        className={styles.filter}
                        items={selectedGruppo.textualFilters!}
                        type={ItemType.checkbox}
                        classNameTitle={styles.wrapperCheckboxTitle}
                        clickAction={onFilterClickHandler}
                        // checkedItem={selectedItem}
                      />
                    </div>
                  </Modal>
                )}
                <div className={styles.wrapperFiltersEstesa}>
                  {isTruthy(selectedGruppo?.textualFilters?.length) && !isWindowFiltersOpen && (
                    <div className={styles.filterButtonContainer}>
                      <Button size="small" onClick={() => setIsWindowFiltersOpen(true)}>
                        <Icon iconId={IconsEnum.FILTER} className={styles.filterButtonIcon} />
                        Filtri
                      </Button>
                    </div>
                  )}
                  {!isMarcatore && (
                    <ScommesseFilterTertiary
                      className={styles.estesaSwitchWrapper}
                      items={[{ label: estesaSwitchLabel, value: 'Estesa', checked: isVisualizzazioneEstesa }]}
                      type={ItemType.toggle}
                      clickAction={() => {
                        dispatch(setIsVisualizzazioneEstesa(!isVisualizzazioneEstesa));
                      }}
                    />
                  )}
                </div>
              </React.Fragment>
            ) : (
              <Wrapper
                bgColorTransparent
                noPaddingTop
                noPaddingLeft
                noPaddingBottom
                noPaddingRight={isOpen || isDesktopXl}
              >
                <div className={styles.wrapper}>
                  <ScommesseTitle className={styles.text}>{makeYourBetLabel}</ScommesseTitle>
                  <div className={styles.filtersContainer}>
                    <ScommesseAccordionFilterPrimary
                      label={scommessaGroupLabel}
                      selectedFilter={selectedGruppo?.descrizione}
                      selectedFilterTrKey={selectedGruppo?.descrizioneTrKey}
                      hasShowMore={filteredGruppoList.length > 4}
                      isAccordionOpened={selectedPrimaryFilter > 8}
                      hasMarginBotton
                    >
                      <ScommesseFiltersList
                        selectedIndex={selectedPrimaryFilter}
                        onSelectedFilter={onGruppoClickHanlder}
                        items={filteredGruppoList}
                      />
                    </ScommesseAccordionFilterPrimary>
                  </div>
                  {!isMarcatore && (
                    <ScommesseFiltersListSecondary isOpen className={styles.scommesseFilterContainer}>
                      {isTruthy(selectedGruppo?.textualFilters?.length) && (
                        <ScommesseFilterTertiary
                          className={styles.wrapperCheckbox}
                          title={scommessaFilterBy}
                          items={selectedGruppo.textualFilters ?? []}
                          type={ItemType.checkbox}
                          clickAction={onFilterClickHandler}
                          // checkedItem={selectedItem}
                        />
                      )}
                      <ScommesseFilterTertiary
                        title={extendedVisualizationLabel}
                        items={[{ label: estesaSwitchLabel, value: 'Estesa', checked: isVisualizzazioneEstesa }]}
                        type={ItemType.toggle}
                        clickAction={() => {
                          dispatch(setIsVisualizzazioneEstesa(!isVisualizzazioneEstesa));
                        }}
                      />
                    </ScommesseFiltersListSecondary>
                  )}
                </div>
              </Wrapper>
            )}
            <Wrapper
              bgColorTransparent
              noPaddingTop
              noPaddingLeft
              noPaddingBottom
              noPaddingRight={isMobile || isOpen || isDesktopXl}
            >
              {isMarcatore ? (
                <AvvenimentoMarcatoriTable tipoScommessa={tipoScommessaList?.[0]!} />
              ) : (
                tipoScommessaList?.map((tipoScommessa, index) => (
                  <ScommesseConditionalAccordion
                    key={tipoScommessa.key}
                    tipoScommessa={tipoScommessa}
                    avvenimentoKey={avvenimentoKey!}
                    isVisualizzazioneEstesa={isVisualizzazioneEstesa}
                    defaultOpened={index < 3 ? tipoScommessa.descrizione : tipoScommessaList[0]?.descrizione}
                  />
                ))
              )}
            </Wrapper>
            {isTruthy(visualComposer?.length) && (
              <SportComposer
                key={`Composer-${asPath}`}
                cmsData={visualComposer ?? []}
                pageId={id}
                templateSlug="templateAvvenimento"
              />
            )}
          </React.Fragment>
        );
      }
    }

    return (
      <React.Fragment>
        <SportAvvenimentoHeader noPaddingRight={isMobile || isOpen} />
        {objGruppi}
      </React.Fragment>
    );
  };

  return (
    <ScommesseLayout taxes={taxes} hasNoPaddingOnMain>
      <HeaderTitleWithSearch
        title={'Sport'} // TODO Cambiare
        category={Category.Sport}
        labelSearch={isSportSearchEnabled ? 'Cerca' : null}
        titleHighlighted={template?.disciplina ?? disciplina}
        titleHighlightedTrKey={template?.disciplinaTrKey}
        isOnlyDesktop={true}
      />
      {isTruthy(heroBanner?.length) && heroBanner[0].banners && (
        <BannerHeroRenderer pageId={id} heroBanner={heroBanner[0]} />
      )}
      <SportTopNavigation hasFirstElementFixed={!isHappybetSite} isOnlyDesktop={true} />
      <BannerHTML5 url={enBannerHTML5.Sport} />
      <SportInternalLayout disciplina={template?.disciplina ?? disciplina} sportNav={sportNav}>
        <div ref={refContainer}>{renderContent()}</div>
        <Loader key={`loader-${id}-${showLoader}-${asPath}-${isReady}`} isVisible={showLoader} />
        {sportNewsBlock && <SportNews url={sportNewsBlock.url} title={sportNewsBlock.title} news={news ?? []} />}
        <EmptySport
          key={`empty-${id}-${isTemplateLoading}-${isScommessaLoading}-${asPath}-${isReady}`}
          isVisible={showEmpty}
          hasFilter={isNotEmpty(filterValue)}
          hasRedirect={!isTruthy(template?.gruppoList?.length)}
          resetFilter={onGruppoClickHanlder}
        />
        <TabsWithAccordion tabs={seoData?.tabs ?? []} />
      </SportInternalLayout>
    </ScommesseLayout>
  );
};

export default PageContent;
